import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import App from "@/App";

// RESONATE_REDUNDANT_HERO_HASH_STRIP_V1 - a URL fragment (#agency-hero etc.)
// never reaches the server, so it can't be fixed with a server-side
// redirect; there's nothing for one to match. These three ids are internal
// analytics scroll-tracking anchors (TrackedSection, which also avoids
// re-adding them - see the NO_HASH_IDS set there), not real navigation
// targets - landing on them is already the top of the page - so this just
// avoids a flash of the hash being visible for anyone arriving via an old
// link that already has it. MUST run before BrowserRouter mounts (inside
// <App/> below): React Router v7 initializes its own history/location
// state from window.location on mount and doesn't pick up a later,
// out-of-band history.replaceState call - stripping here means the
// router's very first read of the URL is already clean, so there is
// nothing for it to fight over.
const REDUNDANT_HERO_HASH = {
  "#agency-hero": "/agencies",
  "#pricing-hero": "/pricing",
  "#dfy-hero": "/done-for-you",
};
const cleanPath = REDUNDANT_HERO_HASH[window.location.hash];
if (cleanPath && window.location.pathname === cleanPath) {
  window.history.replaceState(null, "", cleanPath + window.location.search);
}

// RESONATE_PERF_HYDRATE_V1 - every indexable route is prerendered to static
// HTML by scripts/ssg.cjs (see build/<route>/index.html). This MUST hydrate
// that markup (ReactDOM.hydrateRoot), not replace it (ReactDOM.createRoot +
// render()). createRoot().render() unconditionally clears the container and
// mounts a fresh client tree - on a route behind React.lazy()/Suspense it
// discards the already-painted SSR content and swaps in the Suspense
// fallback (a spinner) until the route's JS chunk downloads, then swaps the
// real content back in a second time. That's an LCP-defeating "flash to
// spinner": Lighthouse timed FCP at the SSR paint but LCP at the *second*,
// JS-driven repaint minutes later on slow connections (verified locally: the
// features-index root was removed and replaced by the loading spinner ~3.5s
// after the SSR paint, then re-added ~500ms later). hydrateRoot reuses the
// existing DOM instead of discarding it, so the SSR paint IS the LCP paint.
//
// window.__SSG_HYDRATING__ lets components that intentionally render
// differently server vs. client (see adVariants.js's useAdVariant, used by
// Hero/AgencyHero/MobileStickyCta) detect a hydration pass and match the
// server output on their first render instead of triggering a
// hydration-mismatch discard-and-rerender.
const container = document.getElementById("root");
const isHydrating = container.hasChildNodes();
window.__SSG_HYDRATING__ = isHydrating;
const app = (
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
if (isHydrating) {
  ReactDOM.hydrateRoot(container, app);
} else {
  // No prerendered markup (e.g. local `yarn start` dev server) - fall back
  // to a plain client render.
  ReactDOM.createRoot(container).render(app);
}
